import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SelfHostedInstanceCard } from "../../components/dashboard/SelfHostedInstanceCard";
import { RegisterSelfHostedDialog } from "../../components/dashboard/RegisterSelfHostedDialog";
import { EmptyState } from "../../components/dashboard";
import { getSelfHostedInstances } from "../../lib/api/cloud";
import type { SelfHostedInstance } from "../../types/cloud";

export function SelfHostedInstances() {
  const { t } = useTranslation();
  const [instances, setInstances] = useState<SelfHostedInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchInstances = async () => {
    try {
      const data = await getSelfHostedInstances();
      setInstances(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('dashboard.selfHosted.loadFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  // Poll for status updates when any instance is in a transitional state
  useEffect(() => {
    const hasTransitional = instances.some((i) =>
      ["pending", "connecting"].includes(i.status)
    );
    if (!hasTransitional) return;

    const interval = setInterval(fetchInstances, 5000);
    return () => clearInterval(interval);
  }, [instances.map((i) => `${i.id}:${i.status}`).join(",")]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#f5f5f5]">
            {t('dashboard.selfHosted.title')}
          </h1>
          <p className="text-[#999] text-sm mt-1">
            {t('dashboard.selfHosted.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setDialogOpen(true)}
          className="px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white rounded-lg text-sm font-medium transition-all"
        >
          {t('dashboard.selfHosted.register')}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm mb-2">{error}</p>
          <button
            onClick={fetchInstances}
            className="text-red-400 text-sm underline hover:text-red-300"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {instances.length === 0 && !error ? (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-8">
          <EmptyState
            icon={
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5 12h14M12 5l7 7-7 7"
                />
              </svg>
            }
            title={t('dashboard.selfHosted.emptyTitle')}
            description={t('dashboard.selfHosted.emptyDescription')}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {instances.map((instance) => (
            <SelfHostedInstanceCard
              key={instance.id}
              instance={instance}
              onRefresh={fetchInstances}
            />
          ))}
        </div>
      )}

      <RegisterSelfHostedDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          setDialogOpen(false);
          fetchInstances();
        }}
      />
    </div>
  );
}
